import type { CSSProperties } from 'react';

import { LogoIcon } from '@/components/app-logo/logo-icon';
import { LogoText } from '@/components/app-logo/logo-text';

import { DEFAULT_TIER_CARD_APPEARANCE, type PricingTierCardAppearance, type PricingTierCardThemeColors } from './types';

const ACCENT_LIGHT = '#14213D';
const ACCENT_DARK = '#F5F5F5';
const MUTED_LIGHT = '#6E6E6E';
const MUTED_DARK = '#A0A0A0';
const BORDER_LIGHT = '#E5E5E5';
const BORDER_DARK = '#2a2a2a';

export type MembershipCardOrientation = 'horizontal' | 'vertical';

export interface MembershipCardPreviewProps {
  appearance: PricingTierCardAppearance | null;
  theme: 'light' | 'dark';
  tierName: string | null;
  userName: string;
  expiresAt: string | null;
  isFamily?: boolean;
  labels: {
    validUntil: string;
    validity: string;
    lifetime: string;
    family?: string;
  };
  locale?: string;
  orientation?: MembershipCardOrientation;
  className?: string;
  /** Стабильный seed для процедурного паттерна (по умолчанию userName|tierName). */
  patternSeed?: string;
}

type PatternShape =
  | 'circle'
  | 'triangle'
  | 'diamond'
  | 'rect'
  | 'semicircle'
  | 'semiellipse';

const PATTERN_SHAPES: readonly PatternShape[] = [
  'circle',
  'triangle',
  'diamond',
  'rect',
  'semicircle',
  'semiellipse',
];

function hashSeed(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface PatternCell {
  x: number;
  y: number;
  shape: PatternShape;
  isPrimary: boolean;
}

function generateCells(
  positions: ReadonlyArray<{ x: number; y: number }>,
  seed: string,
): PatternCell[] {
  const rand = mulberry32(hashSeed(seed));
  return positions.map(({ x, y }, index) => ({
    x,
    y,
    shape: PATTERN_SHAPES[Math.floor(rand() * PATTERN_SHAPES.length)] ?? 'circle',
    isPrimary: index % 2 === 0,
  }));
}

interface PatternFigureProps {
  shape: PatternShape;
  color: string;
}

function PatternFigure({ shape, color }: PatternFigureProps) {
  switch (shape) {
    case 'circle':
      return <circle cx="18" cy="18" r="18" fill={color} />;
    case 'triangle':
      return <polygon points="0,36 36,36 18,0" fill={color} />;
    case 'diamond':
      return <polygon points="18,0 36,18 18,36 0,18" fill={color} />;
    case 'rect':
      return <rect x="0" y="0" width="36" height="36" fill={color} />;
    case 'semicircle':
      return <path d="M0,18 A18,18 0 0 1 36,18 L0,18 Z" fill={color} />;
    case 'semiellipse':
      return <path d="M0,18 A18,18 0 0 0 36,18 L0,18 Z" fill={color} />;
    default:
      return null;
  }
}

const HORIZONTAL_POSITIONS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 0, y: -17 }, { x: 0, y: 33 }, { x: 0, y: 83 }, { x: 0, y: 133 }, { x: 0, y: 183 },
  { x: 42, y: -30 }, { x: 42, y: 20 }, { x: 42, y: 70 }, { x: 42, y: 120 }, { x: 42, y: 170 },
  { x: 84, y: -10 }, { x: 84, y: 40 }, { x: 84, y: 90 }, { x: 84, y: 140 }, { x: 84, y: 190 },
];

const VERTICAL_POSITIONS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 8, y: 0 }, { x: 60, y: 8 }, { x: 112, y: 0 }, { x: 164, y: 8 }, { x: 216, y: 0 }, { x: 268, y: 8 },
  { x: 8, y: 54 }, { x: 60, y: 46 }, { x: 112, y: 54 }, { x: 164, y: 46 }, { x: 216, y: 54 }, { x: 268, y: 46 },
  { x: 8, y: 92 }, { x: 60, y: 100 }, { x: 112, y: 92 }, { x: 164, y: 100 }, { x: 216, y: 92 }, { x: 268, y: 100 },
];

function MembershipPattern({
  primary,
  secondary,
  variant,
  seed,
}: {
  primary: string;
  secondary: string;
  variant: 'horizontal' | 'vertical';
  seed: string;
}) {
  if (variant === 'horizontal') {
    const cells = generateCells(HORIZONTAL_POSITIONS, `h:${seed}`);
    return (
      <svg
        viewBox="0 0 126 200"
        preserveAspectRatio="xMidYMid meet"
        className="pointer-events-none absolute right-0 top-0 h-full w-[126px]"
        aria-hidden="true"
      >
        {cells.map((cell) => (
          <g key={`${cell.x}-${cell.y}`} transform={`translate(${cell.x},${cell.y})`}>
            <PatternFigure shape={cell.shape} color={cell.isPrimary ? primary : secondary} />
          </g>
        ))}
      </svg>
    );
  }

  const cells = generateCells(VERTICAL_POSITIONS, `v:${seed}`);
  return (
    <svg
      viewBox="0 0 315 126"
      preserveAspectRatio="xMidYMax slice"
      className="pointer-events-none absolute inset-x-0 bottom-0 h-[30%] w-full"
      aria-hidden="true"
    >
      {cells.map((cell) => (
        <g key={`${cell.x}-${cell.y}`} transform={`translate(${cell.x},${cell.y})`}>
          <PatternFigure shape={cell.shape} color={cell.isPrimary ? primary : secondary} />
        </g>
      ))}
    </svg>
  );
}

function MastersClubLogo({ color }: { color: string }) {
  return (
    <div
      className="flex items-center gap-2"
      style={{ color }}
      aria-label="MASTERS CLUB"
    >
      <LogoIcon className="size-7" strokeWidth={0} />
      <LogoText className="h-[21px] w-auto" />
    </div>
  );
}

function isDarkColor(hex: string): boolean {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6 && normalized.length !== 3) { return false; }
  const expand = normalized.length === 3
    ? [...normalized].map((c) => c + c).join('')
    : normalized;
  const r = parseInt(expand.slice(0, 2), 16);
  const g = parseInt(expand.slice(2, 4), 16);
  const b = parseInt(expand.slice(4, 6), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance < 0.5;
}

function pickAppearance(
  appearance: PricingTierCardAppearance | null,
  theme: 'light' | 'dark',
): PricingTierCardThemeColors {
  if (appearance) { return appearance[theme]; }
  return DEFAULT_TIER_CARD_APPEARANCE[theme];
}

function formatExpiryDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return new Date(iso).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}

/**
 * Live-preview карточки абонемента для админки. Идентичен по визуалу
 * `MembershipCardView` на платформе. Дублирование осознанное — в монорепо нет
 * shared ui-kit, оба компонента поддерживаются вручную по правке Figma 2421-3535.
 */
export function MembershipCardPreview({
  appearance,
  theme,
  tierName,
  userName,
  expiresAt,
  isFamily = false,
  labels,
  locale = 'ru-RU',
  orientation = 'horizontal',
  className,
  patternSeed,
}: MembershipCardPreviewProps) {
  const seed = patternSeed ?? `${userName}|${tierName ?? ''}`;
  const palette = pickAppearance(appearance, theme);
  const bgIsDark = isDarkColor(palette.bg);
  const accentColor = bgIsDark ? ACCENT_DARK : ACCENT_LIGHT;
  const mutedColor = bgIsDark ? MUTED_DARK : MUTED_LIGHT;
  const borderColor = bgIsDark ? BORDER_DARK : BORDER_LIGHT;

  const isLifetime = !expiresAt;
  const formattedDate = expiresAt ? formatExpiryDate(expiresAt, locale) : null;

  const containerStyle: CSSProperties = {
    backgroundColor: palette.bg,
    borderColor,
    color: accentColor,
  };

  const dimensions = orientation === 'horizontal'
    ? 'h-[200px] w-[358px] flex-row'
    : 'h-[420px] w-[315px] flex-col';

  return (
    <div
      className={`relative overflow-hidden rounded-xl border px-[22px] py-4 ${dimensions} ${className ?? ''}`}
      style={containerStyle}
    >
      <MembershipPattern
        primary={palette.patternPrimary}
        secondary={palette.patternSecondary}
        variant={orientation}
        seed={seed}
      />

      {isFamily && labels.family && (
        <span
          className="absolute right-3 top-3 z-10 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          style={{
            backgroundColor: bgIsDark ? 'rgba(255,255,255,0.12)' : 'rgba(20,33,61,0.08)',
            color: accentColor,
          }}
        >
          {labels.family}
        </span>
      )}

      <div className="absolute left-[22px] top-4 z-10">
        <MastersClubLogo color={accentColor} />
      </div>

      <div
        className={`relative z-10 flex h-full flex-col gap-2 justify-end ${
          orientation === 'vertical' ? 'pb-[calc(30%+24px)]' : ''
        }`}
      >
        {tierName && (
          <p className="text-base font-normal" style={{ color: accentColor }}>
            {tierName}
          </p>
        )}
        <p
          className="text-xl font-bold uppercase tracking-tight"
          style={{ color: accentColor }}
        >
          {userName}
        </p>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-xs font-normal" style={{ color: mutedColor }}>
            {isLifetime ? labels.validity : labels.validUntil}
          </span>
          <span className="text-base font-semibold" style={{ color: accentColor }}>
            {isLifetime ? labels.lifetime : formattedDate}
          </span>
        </div>
      </div>
    </div>
  );
}
